import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useHistory, NavLink } from 'react-router-dom';
import { useLocalizedStrings } from '../../Localization';
import { useOrganizationContext } from '../../Contexts/OrganizationContext';
import { useUserContext } from '../../Contexts/UserContext';
import { Button, Card, DataTable, Tooltip, Icon, useToast } from '@aidkitorg/component-library';
import { TableAction } from '@aidkitorg/component-library/dist/types/src/DataTable';
import { type User } from '@aidkitorg/aidkit/lib/users/UserService';
import { AdminPanelPage } from '../AdminPanelPage';
import { ColumnDef } from '@tanstack/react-table';
import { useDebouncedCallback } from '../../Hooks/Debounce';
import { BreadcrumbProps } from '@aidkitorg/component-library';
import { UserModal } from './components/UserModal';
import { UsersFilter, Filters } from './components/UsersFilter';

// Helper function to check if user is hidden from non-admins
export const isUserHiddenFromNonAdmins = (user: User): boolean => {
  try {
    const metadata = user.metadata ? JSON.parse(user.metadata) : {};
    return metadata.HiddenFromNonAdmins === true;
  } catch (e) {
    return false;
  }
};

export const UserManagementPage: React.FC = () => {
  const L = useLocalizedStrings();
  const location = useLocation();
  const history = useHistory();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const { users } = useUserContext();

  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.settings
    },
    {
      label: L.admin_panel.nav.user_management
    }
  ];

  // Check for URL parameter to auto-open user modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const userUid = searchParams.get('user');

    if (userUid && users.length > 0) {
      const user = users.find(u => u.uid === userUid);
      if (user && (!selectedUser || selectedUser.uid !== userUid)) {
        setSelectedUser(user);
        setModalMode('edit');
        setModalOpen(true);
      }
    }
  }, [location.search, users, selectedUser]);

  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalMode('create');
    setModalOpen(true);
    // Don't add URL param for create mode
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setModalMode('edit');
    setModalOpen(true);

    // Add user UID to URL
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('user', user.uid);
    history.replace(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedUser(null);

    // Remove user param from URL when closing modal
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('user')) {
      searchParams.delete('user');
      const newSearch = searchParams.toString();
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
      history.replace(newUrl);
    }
  };

  const pageActionButtons = [
    <Button key="create-user" onClick={handleCreateUser} icon={<Icon
      name="PlusIcon"
      type="solid"
      role="decorative"
    />}>
      {L.admin_panel.user_management.create_user}
    </Button>
  ];

  return (
    <>
      <AdminPanelPage
        title={L.admin_panel.nav.user_management}
        breadcrumbs={breadcrumbs}
        pageActionButtons={pageActionButtons}
      >
        <UsersList onEditUser={handleEditUser} />
      </AdminPanelPage>

      {/* User Modal */}
      <UserModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        user={selectedUser}
        mode={modalMode}
      />
    </>
  );
};

type UsersListProps = {
  onEditUser: (user: User) => void;
};

function UsersList({ onEditUser }: UsersListProps) {
  const location = useLocation();
  const history = useHistory();
  const [search, setSearch] = useState<string>('');
  const [filters, setFilters] = useState<Filters>({
    user_group: undefined,
  });

  // Compute active filters count automatically
  const activeFilters = (filters.user_group !== undefined ? 1 : 0);

  const { selectedOrganization } = useOrganizationContext();
  const { users, loadUsers, loadEditableTags, isAdmin, deleteUser, resendWelcomeEmail } = useUserContext();
  const L = useLocalizedStrings();
  const { toast } = useToast();

  useEffect(() => {
    loadEditableTags();
  }, [loadEditableTags]);

  // Initialize filters from URL on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const userGroupParam = searchParams.get('user_group');
    const searchParam = searchParams.get('search');

    setFilters({
      user_group: userGroupParam ? parseInt(userGroupParam) : undefined,
    });

    if (searchParam) {
      setSearch(searchParam);
    }
  }, []);

  // Sync filters to URL whenever they change
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    // Update URL params
    if (filters.user_group === undefined) {
      searchParams.delete('user_group');
    } else {
      searchParams.set('user_group', String(filters.user_group));
    }

    if (search) {
      searchParams.set('search', search);
    } else {
      searchParams.delete('search');
    }

    const newSearch = searchParams.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

    // Only update URL if it's different
    if (location.search !== `?${newSearch}` && location.pathname + location.search !== newUrl) {
      history.replace(newUrl);
    }
  }, [filters, search, location.pathname, history]);

  const debouncedLoadUsers = useDebouncedCallback(
    (organization: string | null | undefined, filterState: Filters, searchVal: string) => {
      loadUsers({
        organization: organization,
        filter: searchVal || undefined,
        user_group: filterState.user_group,
      });
    },
    300,
    [loadUsers]
  );

  useEffect(() => {
    const organizationFilter = selectedOrganization === null ? null : selectedOrganization === undefined ? undefined : selectedOrganization.uid;
    debouncedLoadUsers(organizationFilter, filters, search);
  }, [selectedOrganization, filters, search]);

  const handleSearchChange = (searchValue: string) => {
    setSearch(searchValue);
  };

  const columns = useMemo(() => {
    const baseColumns: ColumnDef<User>[] = [
      {
        header: L.user.name,
        accessorKey: 'name',
      },
      {
        header: L.user.email,
        accessorKey: 'email',
      },
      {
        header: L.user.phone,
        accessorKey: 'phone',
      },
      {
        header: L.user.user_group,
        accessorKey: 'user_group_name',
        cell: ({ row }: any) => {
          const user = row.original as User;
          const userGroupName = user.user_group_name;
          const userGroupId = user.user_group;

          if (!userGroupName || !userGroupId) {
            return '—';
          }

          return (
            <NavLink
              to={`/admin-panel/user-management/user-groups/${userGroupId}`}
            >
              {userGroupName}
            </NavLink>
          );
        }
      },
      {
        header: L.user.organization,
        accessorKey: 'organization_name',
      },
    ];

    // Add Hidden column for admins only
    if (isAdmin) {
      baseColumns.push({
        header: () => (
          <Tooltip text={L.user.hidden_from_non_admins} asChild={true}>
            <span>{L.user.hidden}</span>
          </Tooltip>
        ),
        accessorKey: 'hidden',
        cell: ({ row }: any) => {
          const user = row.original as User;
          const isHidden = isUserHiddenFromNonAdmins(user);
          return (
            <div className="flex items-center justify-center">
              {isHidden ? (
                <Icon name="CheckIcon" className="w-4 h-4" />
              ) : null}
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [L, isAdmin]);

  const handleDeleteUser = async (user: User) => {
    if (!confirm(L.user.confirm_delete_user)) {
      return;
    }

    const success = await deleteUser(user.uid);
    if (success) {
      // Refresh the table with current filters
      const organizationFilter = selectedOrganization === null ? null : selectedOrganization === undefined ? undefined : selectedOrganization.uid;
      loadUsers({
        organization: organizationFilter,
        filter: search || undefined,
        user_group: filters.user_group,
      });
    }
  };

  const handleResendWelcomeEmail = async (user: User) => {
    try {
      const success = await resendWelcomeEmail(user.uid);
      if (success) toast({
        title: 'Success',
        description: 'Welcome email sent successfully',
        variant: 'success'
      });
    } catch (error) {
      console.error('Failed to resend welcome email:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send welcome email';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'error'
      });
    }
  };

  const actions = useMemo((): TableAction<User>[] => [
    {
      label: L.admin_panel.user_management.view_user,
      onClick: onEditUser,
      icon: 'PencilIcon'
    },
    {
      label: L.user.delete_user,
      onClick: handleDeleteUser,
      icon: 'TrashIcon',
      hideWhen: (user) => user.can_edit === false
    },
    {
      label: L.user.resend_welcome_email,
      onClick: handleResendWelcomeEmail,
      icon: 'EnvelopeIcon',
      hideWhen: (user) => user.can_edit === false
    }
  ], [handleDeleteUser, handleResendWelcomeEmail, onEditUser, L]);

  return (
    <div className="w-full">
      <Card>
        <DataTable
          columns={columns}
          data={users}
          actions={actions}
          enableSearch={true}
          searchLabel={L.user.search}
          manualSearch={{
            searchValue: search,
            onSearchChange: handleSearchChange,
            resetPageOnChange: true
          }}
          filters={(table) => (
            <UsersFilter
              filters={filters}
              setFilters={setFilters}
              table={table}
              selectedOrganization={selectedOrganization}
            />
          )}
          activeFilters={activeFilters}
          getRowId={(row: User, index: number) => {
            return row.uid ?? String(index);
          }}
        />
      </Card>
    </div>
  );
}

export default UserManagementPage;